// FORGE Trailer — Player-Engine (Szenen, Sprecher, Untertitel)
import './trailer.css';
import { TR } from './trailer-i18n.js';
import { trailerHTML } from './trailer-scenes.js';
import { initAudio, audioSuspend, stopPad, sceneAudio, duck } from './trailer-audio.js';
import { launchTokens, fillLaunch } from './trailer-date.js';
import { createNarrator } from './trailer-voice.js';

// launch = Launch-Datum aus cs_content (CRM) — Badge, Untertitel und Sprecher folgen ihm
export function initTrailer(lang, launch) {
  const tx = fillLaunch(TR[lang] || TR.de, launchTokens(launch, lang));
  const host = document.getElementById('tr-mount');
  if (!host) return;
  host.innerHTML = trailerHTML(tx);

  const narrator = createNarrator(lang);
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scenes = Array.from(host.querySelectorAll('.tr-scene'));
  const durs = scenes.map(s => parseFloat(s.dataset.dur) * 1000);
  const total = durs.reduce((a, b) => a + b, 0);
  const stage = document.getElementById('tr-stage');
  const subEl = document.getElementById('tr-subtitle');

  let idx = -1, sceneStart = 0, elapsedBefore = 0;
  let playing = false, started = false, muted = false, voDone = true, typeTimer = null;

  const segsEl = document.getElementById('tr-segs');
  const fills = scenes.map((s, i) => {
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'tr-seg'; b.style.setProperty('--dur', durs[i]);
    b.setAttribute('aria-label', tx.scenes[s.dataset.key].name);
    b.innerHTML = '<i><b></b></i>';
    b.addEventListener('click', () => goTo(i, true));
    segsEl.appendChild(b);
    return b.firstChild.firstChild;
  });

  function speak(scene) {
    voDone = true;
    narrator.cancel();
    const full = tx.scenes[scene.dataset.key].vo || '';
    if (muted || !full) return;
    voDone = false;
    duck(true);
    narrator.speak(full,
      // Untertitel folgt dem gesprochenen Satz statt den ganzen Block zu zeigen
      line => { subEl.textContent = line; },
      () => { voDone = true; duck(false); subEl.textContent = full; });
  }

  const fmt = ms => { const s = Math.max(0, Math.round(ms / 1000)); return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0'); };
  document.getElementById('tr-total').textContent = fmt(total);

  function startTyping() {
    clearInterval(typeTimer);
    const el = document.getElementById('tr-typed');
    if (!el) return;
    el.textContent = '';
    if (reduced) { el.textContent = tx.typed; return; }
    let n = 0;
    typeTimer = setInterval(() => {
      n++; el.textContent = tx.typed.slice(0, n);
      if (n >= tx.typed.length) clearInterval(typeTimer);
    }, 42);
  }

  function animateCounters(scene) {
    scene.querySelectorAll('.tr-cnt').forEach(el => {
      const to = parseInt(el.dataset.to, 10), t0 = performance.now(), dur = reduced ? 1 : 1400;
      const step = t => {
        let p = Math.min(1, (t - t0) / dur); p = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(to * p);
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  }

  function goTo(i, manual) {
    if (i >= scenes.length) { setPlaying(false); goTo(0, true); return; }
    if (i < 0) i = scenes.length - 1;
    if (idx >= 0) scenes[idx].classList.remove('active');
    idx = i;
    void scenes[idx].offsetWidth;
    scenes[idx].classList.add('active');
    document.getElementById('tr-name').textContent = (idx + 1) + ' · ' + tx.scenes[scenes[idx].dataset.key].name;
    subEl.textContent = tx.scenes[scenes[idx].dataset.key].vo || '';
    elapsedBefore = durs.slice(0, idx).reduce((a, b) => a + b, 0);
    sceneStart = performance.now();
    fills.forEach((f, j) => { f.style.width = j < idx ? '100%' : '0%'; });
    if (scenes[idx].querySelector('#tr-typed')) startTyping();
    if (scenes[idx].querySelector('.tr-cnt')) animateCounters(scenes[idx]);
    if (started && !muted) sceneAudio(idx, scenes[idx].dataset.fx, idx === scenes.length - 1);
    if (started) speak(scenes[idx]);
    if (manual && !playing) render(0);
  }

  function render(inScene) {
    fills[idx].style.width = Math.min(100, inScene / durs[idx] * 100) + '%';
    document.getElementById('tr-time').textContent = fmt(elapsedBefore + Math.min(inScene, durs[idx]));
  }

  function tick() {
    if (playing && idx >= 0) {
      const inScene = performance.now() - sceneStart;
      render(inScene);
      if (inScene >= durs[idx] && voDone) goTo(idx + 1);
    }
    requestAnimationFrame(tick);
  }

  const pp = document.getElementById('tr-pp');
  function setPlaying(p) {
    playing = p;
    pp.textContent = p ? '❚❚' : '▶';
    if (p) narrator.resume(); else narrator.pause();
    audioSuspend(p);
  }
  pp.addEventListener('click', () => {
    if (!started) { start(); return; }
    if (!playing) sceneStart = performance.now() - (parseFloat(fills[idx].style.width) || 0) / 100 * durs[idx];
    setPlaying(!playing);
  });

  const muteBtn = document.getElementById('tr-mute');
  function setMuted(m) {
    muted = m;
    muteBtn.textContent = m ? '🔇' : '🔊';
    if (m) { narrator.cancel(); voDone = true; duck(false); stopPad(); }
    if (!m && started && playing && idx >= 0) { sceneAudio(idx, scenes[idx].dataset.fx, idx === scenes.length - 1); speak(scenes[idx]); }
  }
  muteBtn.addEventListener('click', () => setMuted(!muted));

  // Tasten nur wenn die Bühne fokussiert ist — kein Hijacking der Seite
  stage.addEventListener('keydown', e => {
    if (e.code === 'Space') { e.preventDefault(); pp.click(); }
    if (e.code === 'ArrowRight') goTo(idx + 1, true);
    if (e.code === 'ArrowLeft') goTo(idx - 1, true);
    if (e.code === 'KeyM') setMuted(!muted);
  });

  function start() {
    started = true;
    initAudio();
    document.getElementById('tr-overlay').classList.add('gone');
    stage.focus({ preventScroll: true });
    setPlaying(true);
    goTo(0);
  }
  document.getElementById('tr-play').addEventListener('click', start);

  tick();
}
